import {
  doc,
  collection,
  getDoc,
  getDocFromServer,
  getDocs,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { APP_URL } from '../config/constants';
import type { UserProfile } from '../types/user';
import type {
  PlayerReferralAttribution,
  PlayerReferralPayout,
  CoachReferralPayout,
  CoachReferralSubmission,
  CoachReferralRelationship,
  StoredReferralSession,
} from '../types/referrals';

// ─── Referral code generation ─────────────────────────────────────────────────

/**
 * Deterministic 8-char base-36 code derived from a coachId.
 * Uses djb2 XOR variant — fast, no external deps, collision probability
 * negligible at the expected number of coaches.
 */
export function generateReferralCode(coachId: string): string {
  let h = 5381;
  for (let i = 0; i < coachId.length; i++) {
    h = ((h << 5) + h) ^ coachId.charCodeAt(i);
    h = h >>> 0; // unsigned 32-bit
  }
  return h.toString(36).padStart(8, '0').slice(0, 8);
}

/**
 * Returns the coach's referral code, generating and persisting it on first call.
 */
export async function getOrCreateReferralCode(coachId: string): Promise<string> {
  const userRef = doc(db, 'users', coachId);
  const snap = await getDoc(userRef);
  const existing = snap.data()?.coachProfile?.referralCode as string | undefined;
  if (existing) return existing;

  const code = generateReferralCode(coachId);
  await updateDoc(userRef, { 'coachProfile.referralCode': code });
  return code;
}

/**
 * Resolves a referral code to the owning UserProfile.
 * Queries coachProfile.referralCode on the users collection.
 */
export async function getCoachByReferralCode(code: string): Promise<UserProfile | null> {
  const q = query(
    collection(db, 'users'),
    where('coachProfile.referralCode', '==', code)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { uid: d.id, ...d.data() } as UserProfile;
}

/**
 * Resolves a coach slug to the owning UserProfile.
 * Used by the /r/:slug referral landing route.
 */
export async function getCoachBySlug(slug: string): Promise<UserProfile | null> {
  const q = query(
    collection(db, 'users'),
    where('coachProfile.slug', '==', slug)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { uid: d.id, ...d.data() } as UserProfile;
}

// ─── localStorage passive tracking ───────────────────────────────────────────

const REFERRAL_KEY = 'db_referral';
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Persists the referral to localStorage so it survives browser sessions. */
export function storeReferralInSession(referralCode: string, coachId: string): void {
  const payload: StoredReferralSession = {
    referralCode,
    coachId,
    storedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(REFERRAL_KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable (private browsing, full storage)
  }
}

/** Reads and validates the stored referral. Returns null if absent or expired. */
export function getStoredReferral(): StoredReferralSession | null {
  try {
    const raw = localStorage.getItem(REFERRAL_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw) as StoredReferralSession;
    if (Date.now() - new Date(payload.storedAt).getTime() > REFERRAL_TTL_MS) {
      localStorage.removeItem(REFERRAL_KEY);
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

/** Clears the stored referral after it has been applied to a user record. */
export function clearStoredReferral(): void {
  try {
    localStorage.removeItem(REFERRAL_KEY);
  } catch {
    // ignore
  }
}

// ─── Player referral attribution ──────────────────────────────────────────────

/**
 * Sets the referring coach on a player's record (both at root and inside
 * playerProfile). No-ops if referredBy is already set — first attribution wins.
 */
export async function setPlayerReferral(
  playerId: string,
  attribution: PlayerReferralAttribution
): Promise<void> {
  const userRef = doc(db, 'users', playerId);
  const snap = await getDocFromServer(userRef);
  if (!snap.exists()) return;

  // First attribution wins — never overwrite
  if (snap.data().referredBy) return;

  await updateDoc(userRef, {
    referredBy: attribution,
    'playerProfile.referredBy': attribution,
    updatedAt: new Date().toISOString(),
  });

  clearStoredReferral();
}

/**
 * A new player account takes the referral a /r/:slug link stored, right after
 * its profile is created. Sign-up can go straight to a saved redirect (Book on
 * a coach's page, signed out) and skip the onboarding steps that otherwise
 * apply it. Best effort: a failure never holds the sign-up, and the stored
 * referral stays for onboarding to try again. First attribution wins.
 */
export async function applyStoredReferral(playerId: string): Promise<void> {
  const stored = getStoredReferral();
  if (!stored?.coachId) return;
  try {
    await setPlayerReferral(playerId, {
      coachId: stored.coachId,
      referralCode: stored.referralCode,
      source: 'link',
      setAt: new Date().toISOString(),
    });
  } catch (err) {
    console.warn('[referrals] could not apply the stored referral at sign-up', err);
  }
}

/** Returns the player's referral attribution, or null if not set. */
export async function getPlayerReferral(
  playerId: string
): Promise<PlayerReferralAttribution | null> {
  const snap = await getDoc(doc(db, 'users', playerId));
  return (snap.data()?.referredBy as PlayerReferralAttribution) ?? null;
}

// ─── Player referral payouts ──────────────────────────────────────────────────

/**
 * Called by backend when a referred player completes their first paid lesson.
 * Creates a pending $25 payout record for the referring coach.
 */
export async function createPlayerReferralPayout(
  referringCoachId: string,
  referredPlayerId: string,
  lessonId: string
): Promise<string> {
  const ref = await addDoc(collection(db, 'playerReferralPayouts'), {
    referringCoachId,
    referredPlayerId,
    lessonId,
    status: 'pending',
    amountCents: 2500,
    createdAt: new Date().toISOString(),
    createdAtTs: serverTimestamp(),
  });
  return ref.id;
}

/** Returns all player referral payouts for a coach. */
export async function getPlayerReferralPayouts(coachId: string): Promise<PlayerReferralPayout[]> {
  const q = query(
    collection(db, 'playerReferralPayouts'),
    where('referringCoachId', '==', coachId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as PlayerReferralPayout));
}

/** Admin: returns all pending player referral payouts. */
export async function getAllPlayerReferralPayouts(): Promise<PlayerReferralPayout[]> {
  const snap = await getDocs(collection(db, 'playerReferralPayouts'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as PlayerReferralPayout));
}

/** Admin: marks a player referral payout as paid. */
export async function markPlayerReferralPayoutPaid(payoutId: string): Promise<void> {
  await updateDoc(doc(db, 'playerReferralPayouts', payoutId), {
    status: 'paid',
    paidAt: new Date().toISOString(),
  });
}

// ─── Coach-on-coach referral submissions ─────────────────────────────────────

/** Coach submits a candidate for admin review. */
export async function submitCoachReferral(
  referringCoachId: string,
  data: Pick<CoachReferralSubmission, 'candidateName' | 'candidateEmail' | 'credentials' | 'fitNotes'>
): Promise<string> {
  const ref = await addDoc(collection(db, 'coachReferralSubmissions'), {
    referringCoachId,
    ...data,
    status: 'pending',
    submittedAt: new Date().toISOString(),
    submittedAtTs: serverTimestamp(),
  });

  // Store the active submission ID on the coach's profile
  await updateDoc(doc(db, 'users', referringCoachId), {
    'coachProfile.activeCoachReferralId': ref.id,
  });

  return ref.id;
}

/** Returns a single submission by ID. */
export async function getCoachReferralSubmission(
  submissionId: string
): Promise<CoachReferralSubmission | null> {
  const snap = await getDoc(doc(db, 'coachReferralSubmissions', submissionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as CoachReferralSubmission;
}

/** Admin: returns all pending submissions. */
export async function getPendingCoachReferralSubmissions(): Promise<CoachReferralSubmission[]> {
  const q = query(
    collection(db, 'coachReferralSubmissions'),
    where('status', '==', 'pending'),
    orderBy('submittedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as CoachReferralSubmission));
}

/** Admin: returns all submissions regardless of status. */
export async function getAllCoachReferralSubmissions(): Promise<CoachReferralSubmission[]> {
  const q = query(
    collection(db, 'coachReferralSubmissions'),
    orderBy('submittedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as CoachReferralSubmission));
}

/**
 * Admin: approves a submission and creates the referral relationship.
 * Consumes the slot on the referring coach's profile.
 */
export async function approveCoachReferralSubmission(
  submissionId: string,
  referredCoachId: string,
  adminId: string
): Promise<string> {
  const now = new Date();
  const onboardingDate = now.toISOString();
  const windowEndsAt = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(); // +3 months

  const submissionRef = doc(db, 'coachReferralSubmissions', submissionId);
  const submissionSnap = await getDoc(submissionRef);
  if (!submissionSnap.exists()) throw new Error('Submission not found');
  const { referringCoachId } = submissionSnap.data() as CoachReferralSubmission;

  // Update submission status
  await updateDoc(submissionRef, {
    status: 'accepted',
    referredCoachId,
    reviewedAt: onboardingDate,
    reviewedByAdminId: adminId,
  });

  // Create the revenue-share relationship
  const relRef = await addDoc(collection(db, 'coachReferralRelationships'), {
    referringCoachId,
    referredCoachId,
    onboardingDate,
    windowEndsAt,
    revenueShareRate: 0.11,
    status: 'active',
    totalEarnedCents: 0,
    createdAtTs: serverTimestamp(),
  });

  // Consume the slot — coach must be re-granted by admin for another referral
  await updateDoc(doc(db, 'users', referringCoachId), {
    'coachProfile.hasReferralSlot': false,
    'coachProfile.activeCoachReferralId': null,
  });

  return relRef.id;
}

/** Admin: rejects a submission. Slot remains active. */
export async function rejectCoachReferralSubmission(
  submissionId: string,
  rejectionReason: string,
  adminId: string
): Promise<void> {
  const submissionRef = doc(db, 'coachReferralSubmissions', submissionId);
  const snap = await getDoc(submissionRef);
  if (!snap.exists()) throw new Error('Submission not found');
  const { referringCoachId } = snap.data() as CoachReferralSubmission;

  await updateDoc(submissionRef, {
    status: 'rejected',
    rejectionReason,
    reviewedAt: new Date().toISOString(),
    reviewedByAdminId: adminId,
  });

  // Clear active referral ID so coach can submit again (slot itself stays)
  await updateDoc(doc(db, 'users', referringCoachId), {
    'coachProfile.activeCoachReferralId': null,
  });
}

// ─── Coach referral relationships ─────────────────────────────────────────────

/** Returns the active/completed relationship where this coach is the referrer. */
export async function getCoachReferralRelationship(
  referringCoachId: string
): Promise<CoachReferralRelationship | null> {
  // Equality-only query avoids a composite index on staging Firestore.
  // Client-side sort picks the most recent if there are ever multiple.
  const q = query(
    collection(db, 'coachReferralRelationships'),
    where('referringCoachId', '==', referringCoachId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as CoachReferralRelationship));
  docs.sort((a, b) => b.onboardingDate.localeCompare(a.onboardingDate));
  return docs[0];
}

/** Admin: returns all relationships. */
export async function getAllCoachReferralRelationships(): Promise<CoachReferralRelationship[]> {
  const snap = await getDocs(collection(db, 'coachReferralRelationships'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as CoachReferralRelationship));
}

/** Admin: grant or revoke a coach's referral slot. */
export async function setCoachReferralSlot(coachId: string, hasSlot: boolean): Promise<void> {
  await updateDoc(doc(db, 'users', coachId), {
    'coachProfile.hasReferralSlot': hasSlot,
  });
}

/** Admin: override rate or window on a relationship. */
export async function overrideCoachReferralRelationship(
  relationshipId: string,
  overrides: { rate?: number; windowEndsAt?: string }
): Promise<void> {
  const updates: Record<string, unknown> = {};
  if (overrides.rate !== undefined) updates.overrideRate = overrides.rate;
  if (overrides.windowEndsAt !== undefined) updates.overrideWindowEndsAt = overrides.windowEndsAt;
  await updateDoc(doc(db, 'coachReferralRelationships', relationshipId), updates);
}

/**
 * Calculates the coach-on-coach payout for a bundle delivery.
 * Returns null if no active relationship exists or window has expired.
 * Called by billing logic when a bundle is completed.
 */
export async function calculateCoachReferralPayout(
  deliveredByCoachId: string,
  grossAmountCents: number
): Promise<{ referringCoachId: string; referredCoachId: string; payoutCents: number; rate: number; relationshipId: string } | null> {
  const q = query(
    collection(db, 'coachReferralRelationships'),
    where('referredCoachId', '==', deliveredByCoachId),
    where('status', '==', 'active')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;

  const d = snap.docs[0];
  const rel = { id: d.id, ...d.data() } as CoachReferralRelationship;

  const effectiveRate = rel.overrideRate ?? rel.revenueShareRate;
  const effectiveWindowEnd = new Date(rel.overrideWindowEndsAt ?? rel.windowEndsAt);

  if (new Date() > effectiveWindowEnd) {
    // Window expired — mark completed
    await updateDoc(d.ref, { status: 'completed' });
    return null;
  }

  const payoutCents = Math.round(grossAmountCents * effectiveRate);

  // Accumulate total earned on the relationship record
  await updateDoc(d.ref, {
    totalEarnedCents: (rel.totalEarnedCents ?? 0) + payoutCents,
  });

  return { referringCoachId: rel.referringCoachId, referredCoachId: deliveredByCoachId, payoutCents, rate: effectiveRate, relationshipId: rel.id };
}

/** Creates a pending payout record after a successful coach transfer. */
export async function createCoachReferralPayoutDoc(params: {
  relationshipId: string;
  referringCoachId: string;
  referredCoachId: string;
  lessonId: string;
  grossCents: number;
  payoutCents: number;
  rate: number;
}): Promise<void> {
  await addDoc(collection(db, 'coachReferralPayouts'), {
    ...params,
    status: 'pending',
    createdAt: new Date().toISOString(),
    createdAtTs: serverTimestamp(),
  });
}

// ─── Coach referral payouts ───────────────────────────────────────────────────

/** Returns all coach-on-coach referral payouts for a referring coach. */
export async function getCoachReferralPayouts(referringCoachId: string): Promise<CoachReferralPayout[]> {
  const q = query(
    collection(db, 'coachReferralPayouts'),
    where('referringCoachId', '==', referringCoachId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as CoachReferralPayout));
}

/** Admin: returns all coach referral payouts. */
export async function getAllCoachReferralPayouts(): Promise<CoachReferralPayout[]> {
  const q = query(
    collection(db, 'coachReferralPayouts'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as CoachReferralPayout));
}

// ─── Referral URL builder ─────────────────────────────────────────────────────

export function buildReferralUrl(coachSlug: string): string {
  // APP_URL, not window.location.origin. The origin is wherever the coach
  // happens to be standing, which on a dev machine is http://localhost:5173 —
  // a link that works for nobody. /r/:slug is a PWA route (ReferralLanding,
  // which stores the referral then redirects to /c/:slug); the marketing site
  // has no such route, so this must point at the app rather than the CMS.
  return `${APP_URL}/r/${coachSlug}`;
}

// Suppress unused import warning for Timestamp (reserved for future server-side use)
void Timestamp;
